import React from 'react';
import { DatePicker, Button, Radio } from 'antd';
import { CalendarFilled } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { MultiAgentDatePickerModeEnum } from 'src/enums/EMultiAgentDatePicker';

const { RangePicker } = DatePicker;

interface MultiOptionDatePickerProps {
	pickerMode: MultiAgentDatePickerModeEnum;
	setPickerMode: (mode: MultiAgentDatePickerModeEnum) => void;
	dateValue: Dayjs | null;
	setDateValue: (date: Dayjs | null) => void;
	rangeValue: [Dayjs | null, Dayjs | null];
	setRangeValue: (range: [Dayjs | null, Dayjs | null]) => void;
	showDatePicker: boolean;
	setShowDatePicker: (show: boolean) => void;
	handleDatePickerOk: (clear?: boolean) => void;
}

export const MultiOptionDatePicker: React.FC<MultiOptionDatePickerProps> = ({
	pickerMode,
	setPickerMode,
	dateValue,
	setDateValue,
	rangeValue,
	setRangeValue,
	showDatePicker,
	setShowDatePicker,
	handleDatePickerOk,
}) => {
	const handleSingleChange = (date: Dayjs | null) => {
		setDateValue(date);
		setPickerMode(MultiAgentDatePickerModeEnum.SINGLE);
	};
	const handleRangeChange = (dates: [Dayjs | null, Dayjs | null]) => {
		setRangeValue(dates);
		setPickerMode(MultiAgentDatePickerModeEnum.RANGE);
	};

	const handleClear = () => {
		setDateValue(dayjs());
		setRangeValue([null, null]);
		setShowDatePicker(false);
		handleDatePickerOk(true);
	};

	return (
		<div className="relative">
			<Button
				className="h-fit flex items-center px-4 py-2 rounded-lg border font-semibold text-base transition-all"
				onClick={() => setShowDatePicker(!showDatePicker)}
			>
				{pickerMode === 'range' && rangeValue[0] && rangeValue[1]
					? `${rangeValue[0].format('MMM D, YYYY')} - ${rangeValue[1].format('MMM D, YYYY')}`
					: dateValue?.format('MMM D, YYYY')}
				<span className="ml-2 ">
					{' '}
					<CalendarFilled />{' '}
				</span>
			</Button>
			{showDatePicker && (
				<div className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 mt-2 left-0 min-w-[320px]">
					<Radio.Group
						value={pickerMode}
						onChange={(e) => setPickerMode(e.target.value)}
						className="mb-2"
					>
						<Radio.Button value="single">Single</Radio.Button>
						<Radio.Button value="range">Range</Radio.Button>
					</Radio.Group>
					{pickerMode === 'single' ? (
						<DatePicker
							value={dateValue}
							onChange={handleSingleChange}
							className={
								'w-full mb-2 flex items-center px-4 py-2 rounded-lg border font-semibold text-base transition-all'
							}
							getPopupContainer={() => document.body}
						/>
					) : (
						<RangePicker
							value={rangeValue}
							onChange={handleRangeChange}
							className={
								'w-full mb-2 flex items-center px-4 py-2 rounded-lg border font-semibold text-base transition-all'
							}
							getPopupContainer={() => document.body}
						/>
					)}
					<div className="flex gap-2">
						<Button
							type="primary"
							className="w-full mt-2 bg-violet-600 border-violet-600 hover:bg-violet-700"
							onClick={() => handleDatePickerOk()}
							disabled={pickerMode === 'single' ? !dateValue : !(rangeValue[0] && rangeValue[1])}
						>
							FILTER
						</Button>
						<Button className="w-full mt-2" onClick={handleClear} type="default">
							Clear
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
